import {
    CreateExtArgs,
    CreateExtensionArgument,
    DmApis,
    Extension,
    ExtensionAPI,
    DalValue,
    ValidatorMap,
    ValidatorResult,
    PAGE_FRAGMENT_TYPE,
    pointerUtils
} from '@wix/document-manager-core'
import {ReportableError} from '@wix/document-manager-utils'
import type {NamedRange, Pointer} from '@wix/document-services-types'
import * as constants from '../constants/constants'
import type {DataModelExtensionAPI} from './dataModel/dataModel'
import type {VariantsExtensionAPI} from './variants/variants'
import type {PageExtensionAPI} from './page'
import {stripHashIfExists} from '../utils/refArrayUtils'
import _ from 'lodash'

const {getPointer} = pointerUtils
const {DATA_TYPES, VIEW_MODES} = constants

export type PartialNamedRange = Omit<NamedRange, 'id' | 'type'>
export interface NamedRangesAPI {
    get(namedRangePointer: Pointer): NamedRange
    list(): Pointer[]
    getNewItemPointer(): Pointer
    add(newNamedRangePointer: Pointer, namedRange: PartialNamedRange): void
    update(namedRangePointer: Pointer, namedRange: PartialNamedRange): void
    remove(namedRangePointer: Pointer): void
}

export type NamedRangesExtensionAPI = ExtensionAPI & {
    namedRanges: NamedRangesAPI
}

const createExtensionAPI = ({dal, pointers, extensionAPI}: CreateExtArgs): NamedRangesExtensionAPI => {
    const dataModel = (extensionAPI as DataModelExtensionAPI).dataModel.withSuper
    const {variants} = extensionAPI as VariantsExtensionAPI
    const {page} = extensionAPI as PageExtensionAPI

    const MASTER_PAGE_ID = 'masterPage'
    const breakpointVariantsQuery = 'breakpointVariantsQuery'

    const getMasterPage = () => pointers.structure.getMasterPage(VIEW_MODES.DESKTOP)

    const getBreakpointsQueryPointer = (pageOrMasterpagePointer: Pointer): Pointer =>
        pointers.getInnerPointer(pageOrMasterpagePointer, breakpointVariantsQuery)
    const getBreakpointsDataPointer = (pageOrMasterpagePointer: Pointer): DalValue => {
        const breakpointsQueryPointer = getBreakpointsQueryPointer(pageOrMasterpagePointer)
        if (dal.has(breakpointsQueryPointer)) {
            const dataId = dal.get(breakpointsQueryPointer)
            return pointers.getPointer(dataId, DATA_TYPES.variants)
        }
    }
    const getOrCreateNamedRangesList = (pageOrMasterpagePointer: Pointer): Pointer => {
        const breakpointsDataPointer = getBreakpointsDataPointer(pageOrMasterpagePointer)

        if (breakpointsDataPointer) {
            return breakpointsDataPointer
        }

        const breakpointsData = {
            type: 'BreakpointsData',
            values: [],
            namedRanges: [],
            componentId: pageOrMasterpagePointer.id
        }
        const newItemPointer = dataModel.addItem(breakpointsData, DATA_TYPES.variants, pageOrMasterpagePointer.id)
        const breakpointsQueryPointer = getBreakpointsQueryPointer(pageOrMasterpagePointer)
        dal.set(breakpointsQueryPointer, newItemPointer.id)
        return newItemPointer
    }

    const list = (): Pointer[] => {
        const breakpointsDataPointer = getBreakpointsDataPointer(getMasterPage())
        if (!breakpointsDataPointer) {
            return []
        }

        const breakpointsData = dal.get(breakpointsDataPointer)

        if (!breakpointsData.namedRanges || breakpointsData.namedRanges.length === 0) {
            return []
        }

        return breakpointsData.namedRanges.map((ref: string) =>
            pointers.data.getVariantsDataItem(stripHashIfExists(ref))
        )
    }

    const get = (namedRangePointer: Pointer): NamedRange => {
        const namedRange = dal.get(namedRangePointer)

        if (!namedRange || namedRange.type !== 'NamedRange') {
            throw new ReportableError({
                message: `NamedRange ${namedRangePointer.id} does not exist`,
                errorType: 'nonExistentNamedRange'
            })
        }

        return namedRange as NamedRange
    }

    const validateNamedRange = (namedRange: Partial<NamedRange>) => {
        if (!namedRange.name) {
            throw new ReportableError({
                message: 'NamedRange name is required',
                errorType: 'invalidNamedRange'
            })
        }

        if (namedRange.name.length > 30) {
            throw new ReportableError({
                message: 'NamedRange name must be at most 30 characters',
                errorType: 'invalidNamedRange'
            })
        }

        if (!namedRange.spx) {
            throw new ReportableError({
                message: 'NamedRange spx is required',
                errorType: 'invalidNamedRange'
            })
        }

        if (!namedRange.spx.refWidth || namedRange.spx.refWidth < 1) {
            throw new ReportableError({
                message: 'NamedRange spx.refWidth must be a positive integer',
                errorType: 'invalidNamedRange'
            })
        }
    }

    const validateNameUniqueness = (name: string, excludeId?: string) => {
        const existingPointers = list()
        const itemWithThisName = existingPointers.find(ptr => {
            return (dal.get(ptr) as NamedRange).name === name
        })

        if (itemWithThisName && !(excludeId && itemWithThisName.id === excludeId)) {
            throw new ReportableError({
                message: `NamedRange with name '${name}' already exists`,
                errorType: 'duplicateNamedRange'
            })
        }
    }

    const validateNamedRangeInScope = (namedRangePointer: Pointer, scope: Pointer) => {
        const breakpointsDataPointer = getBreakpointsDataPointer(scope)
        if (breakpointsDataPointer) {
            const breakpointsData = dal.get(breakpointsDataPointer)
            const namedRangeRef = `#${namedRangePointer.id}`
            if (breakpointsData.namedRanges.includes(namedRangeRef)) {
                return
            }
        }

        throw new ReportableError({
            message: `NamedRange ${namedRangePointer.id} does not exist for ${scope.id}`,
            errorType: 'namedRangeNotInScope'
        })
    }

    const clearDefaultRangeFromAll = (): void => {
        list().forEach(ptr => {
            const item = dal.get(ptr) as NamedRange
            if (item && item.defaultRange === true) {
                const cleared = _.omit(item, 'defaultRange')
                dal.set(ptr, cleared)
            }
        })
    }

    const getNewItemPointer = () => {
        const namedRangeId = dataModel.generateUniqueIdByType(DATA_TYPES.variants, MASTER_PAGE_ID, dal, pointers)
        return getPointer(namedRangeId, DATA_TYPES.variants)
    }

    const add = (newNamedRangePointer: Pointer, namedRange: PartialNamedRange): void => {
        validateNamedRange(namedRange)
        validateNameUniqueness(namedRange.name)

        if (namedRange.defaultRange === true) {
            clearDefaultRangeFromAll()
        }

        const {id: namedRangeId} = newNamedRangePointer
        const fullNamedRange: NamedRange = {
            type: 'NamedRange',
            id: namedRangeId,
            ...namedRange
        }
        if (fullNamedRange.defaultRange !== true) {
            delete fullNamedRange.defaultRange
        }

        dataModel.addItem(fullNamedRange, DATA_TYPES.variants, MASTER_PAGE_ID, namedRangeId)

        const breakpointsDataPointer = getOrCreateNamedRangesList(getMasterPage())
        const breakpointsData = dal.get(breakpointsDataPointer) as any

        const updatedValues = [...(breakpointsData.namedRanges || []), `#${namedRangeId}`]
        dal.set(breakpointsDataPointer, {
            ...breakpointsData,
            namedRanges: updatedValues
        })
    }

    const update = (namedRangePointer: Pointer, namedRange: PartialNamedRange): void => {
        validateNamedRangeInScope(namedRangePointer, getMasterPage())
        validateNamedRange(namedRange)
        validateNameUniqueness(namedRange.name, namedRangePointer.id)

        const existingNamedRange = get(namedRangePointer)

        // Only `defaultRange === true` is a meaningful caller intent. Anything else
        // (omitted, undefined, or `false` via `as any`) preserves the existing value
        // — we never persist `false` and never strip an existing default through update.
        const sanitizedPartial: PartialNamedRange =
            namedRange.defaultRange === true ? namedRange : _.omit(namedRange, 'defaultRange')

        if (sanitizedPartial.defaultRange === true) {
            clearDefaultRangeFromAll()
        }

        const updatedNamedRange: NamedRange = {
            ...existingNamedRange,
            ...sanitizedPartial,
            type: 'NamedRange',
            id: existingNamedRange.id
        }

        dal.set(namedRangePointer, updatedNamedRange)
    }

    const clearNamedRangeOnReferencingBreakpointRanges = (namedRangeId: string): void => {
        const refForm = `#${namedRangeId}`
        const matches = (v: any) =>
            v.type === 'BreakpointRange' && (v.namedRange === namedRangeId || v.namedRange === refForm)
        for (const pageId of page.getAllPagesIds(true)) {
            const referencingPointers = pointers.data.getVariantItemsWithPredicate(matches, pageId)
            for (const bpRangePointer of referencingPointers) {
                const bpRange = dal.get(bpRangePointer) as any
                dal.set(bpRangePointer, _.omit(bpRange, 'namedRange'))
            }
        }
    }

    const remove = (namedRangePointer: Pointer): void => {
        validateNamedRangeInScope(namedRangePointer, getMasterPage())
        const namedRange = get(namedRangePointer)

        if (namedRange.defaultRange === true) {
            const remainingPointers = list().filter(ptr => ptr.id !== namedRangePointer.id)
            if (remainingPointers.length > 0) {
                throw new ReportableError({
                    message: `NamedRange ${namedRange.name} is the current default and cannot be removed while other NamedRanges exist. Promote another NamedRange to default first.`,
                    errorType: 'cannotRemoveDefaultNamedRange'
                })
            }
        }

        variants.removeScopedValuesByVariants([namedRangePointer])

        clearNamedRangeOnReferencingBreakpointRanges(namedRangePointer.id)

        const breakpointsDataPointer = getBreakpointsDataPointer(getMasterPage())
        const namedRangesList = breakpointsDataPointer ? (dal.get(breakpointsDataPointer) as any) : null
        if (namedRangesList) {
            const updatedValues = _.without(namedRangesList.namedRanges, `#${namedRangePointer.id}`)
            dal.set(breakpointsDataPointer, {...namedRangesList, namedRanges: updatedValues})
        }

        dal.remove(namedRangePointer)
    }

    return {
        //if we add a page-level api for namedRanges, we will need to inject the masterPage pointer for the site level ones
        namedRanges: {
            get,
            list,
            getNewItemPointer,
            add,
            update,
            remove
        }
    }
}

const createExtension = ({}: CreateExtensionArgument): Extension => {
    const createValidator = ({dal, pointers}: DmApis): ValidatorMap => ({
        validateNamedRangeDefaultPresence: (pointer: Pointer, value: DalValue): ValidatorResult[] => {
            if (!value || value.type !== 'BreakpointsData') {
                return []
            }

            const refs: string[] = value.namedRanges ?? []
            if (refs.length === 0) {
                return []
            }

            const defaultCount = refs.filter(ref => {
                const id = stripHashIfExists(ref)
                const item = dal.get(pointers.getPointer(id, DATA_TYPES.variants))
                return item?.defaultRange === true
            }).length

            if (defaultCount === 0) {
                return [
                    {
                        shouldFail: true,
                        type: 'namedRangesMissingDefault',
                        message: `BreakpointsData ${pointer.id} has ${refs.length} NamedRange(s) but none has defaultRange: true`,
                        extras: {pointer}
                    }
                ]
            }

            if (defaultCount > 1) {
                return [
                    {
                        shouldFail: true,
                        type: 'namedRangesMultipleDefaults',
                        message: `BreakpointsData ${pointer.id} has ${defaultCount} NamedRanges with defaultRange: true; at most one is allowed`,
                        extras: {pointer}
                    }
                ]
            }

            return []
        }
    })

    return {
        name: 'namedRanges',
        dependencies: new Set(['dataModel', 'variants', 'page', 'dal']),
        createExtensionAPI,
        createValidator,
        validatorScope: {
            include: [PAGE_FRAGMENT_TYPE],
            exclude: []
        },
        createPublicAPI: ({extensionAPI}: DmApis) => {
            const {namedRanges} = extensionAPI as NamedRangesExtensionAPI

            return {
                site: {
                    namedRanges: {
                        get: namedRanges.get,
                        list: namedRanges.list,
                        add: namedRanges.add,
                        update: namedRanges.update,
                        remove: namedRanges.remove,
                        getNewItemPointer: namedRanges.getNewItemPointer
                    }
                }
            }
        }
    }
}

export {createExtension}
